import { useEffect, useState } from "react";

import { useTheme, type Theme } from "../context/theme-context";
import CodeMirrorView from "./CodeMirrorView";
import type { CodeLanguage } from "./lib/code-language";
import { formatCodeForDisplay } from "./lib/format-code";

type Props = {
  readonly children: string;
  readonly language: CodeLanguage;
  readonly theme?: Theme;
};

export default function CodeHighlighter({
  children,
  language,
  theme: themeProp,
}: Props) {
  const contextTheme = useTheme();
  const theme = themeProp ?? contextTheme;
  const source = children.trim();
  const [formatted, setFormatted] = useState<{
    source: string;
    language: CodeLanguage;
    text: string;
  } | null>(null);
  const doc =
    formatted?.source === source && formatted.language === language
      ? formatted.text
      : source;

  useEffect(() => {
    let cancelled = false;

    void formatCodeForDisplay(language, source)
      .catch(() => source)
      .then((text) => {
        if (!cancelled) {
          setFormatted({ source, language, text });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [language, source]);

  return (
    <div
      className="h-full min-h-20 overflow-hidden font-mono text-sm"
      data-theme={theme}
    >
      <CodeMirrorView
        value={doc}
        language={language}
        theme={theme}
        readOnly
        ariaLabel="Code"
      />
    </div>
  );
}
